import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '@/shared/hooks';
import type { JoinedTournament } from "@/shared";
import { leaveSession, fetchUpcomingJoinedSessions } from '@/shared/api';
import { GAME, GameState } from "@/shared/constants/game";
import JoinLeaveModal from '@/components/JoinLeaveModal';

// Jumping dots animation component
function JumpingDots() {
    return (
        <span className="jumping-dots">
            <span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
            <style>{`
                .jumping-dots {
                    display: inline-flex;
                    margin-left: 1px;
                }
                .jumping-dots .dot {
                    animation: jump 1.4s ease-in-out infinite;
                    font-weight: bold;
                }
                .jumping-dots .dot:nth-child(1) { animation-delay: 0s; }
                .jumping-dots .dot:nth-child(2) { animation-delay: 0.2s; }
                .jumping-dots .dot:nth-child(3) { animation-delay: 0.4s; }
                @keyframes jump {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-4px); }
                }
            `}</style>
        </span>
    );
}

function CountdownTimer({ date, time }: { date?: string; time?: string }) {
    const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);

    useEffect(() => {
        if (!date || !time) return;

        const parseDateTime = (): Date | null => {
            try {
                // date: "2026-06-05T00:00:00Z", time: "19:00:00"
                const dateOnly = date.split('T')[0]; // "2026-06-05"
                const [year, month, day] = dateOnly.split('-').map(Number);
                const [hours, minutes] = time.split(':').map(Number);
                // Construct date in UTC (times are stored in UTC)
                return new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
            } catch {
                return null;
            }
        };

        const calcTimeLeft = () => {
            const target = parseDateTime();
            if (!target) return null;
            const diff = target.getTime() - Date.now();
            if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0 };
            return {
                hours: Math.floor(diff / (1000 * 60 * 60)),
                minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((diff % (1000 * 60)) / 1000)
            };
        };

        setTimeLeft(calcTimeLeft());
        const timer = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
        return () => clearInterval(timer);
    }, [date, time]);

    if (!date || !time || !timeLeft) return null;
    if (timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0) {
        return <span>Starting soon<JumpingDots /></span>;
    }

    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
        <span>
            {timeLeft.hours > 0 && `${timeLeft.hours}h `}
            {pad(timeLeft.minutes)}m {pad(timeLeft.seconds)}s
        </span>
    );
}

function JoinedSessions({ gameState } : { gameState: GameState }){
    const [joinedSessions, setJoinedSessions] = useState<JoinedTournament[]>([]);
    const [takeToLobby, setTakeToLobby] = useState<number | null>(null);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [selectedTournament, setSelectedTournament] = useState<JoinedTournament | null>(null);
    const { firebaseUser, loading } = useAuth();
    const navigate = useNavigate();

    const openLeaveModal = (tourney: JoinedTournament) => {
        setSelectedTournament(tourney);
        setShowLeaveModal(true);
    };

    const closeLeaveModal = () => {
        setShowLeaveModal(false);
        setSelectedTournament(null);
    };

    const handleLeave = async () => {
        if (loading || !firebaseUser || !selectedTournament) {
            return;
        }

        try {
            const idToken = await firebaseUser.getIdToken();

            await leaveSession(gameState, selectedTournament.id, idToken);

            setJoinedSessions(prev =>
                prev.filter(t => t.id !== selectedTournament.id)
            );

            closeLeaveModal();
            
            navigate(`/${gameState.toLowerCase()}`);
        } catch (err) {
            console.error(err);
            alert('Failed to leave tournament.');
        }
    };

    useEffect(() => {
    if (takeToLobby !== null) {
        if (gameState === GAME.POOL) {
            navigate(`/pool/sessions/${takeToLobby}`);
        } else {
            navigate(`/${gameState.toLowerCase()}/${takeToLobby}`);
        }
    }
    }, [gameState, navigate, takeToLobby]);

    useEffect(() => {
        const fetchTournaments = async () => {
            if (loading || !firebaseUser) {
                setJoinedSessions([]);
                return;
            }

            try {
                const idToken = await firebaseUser.getIdToken();
                const rawData = await fetchUpcomingJoinedSessions(idToken, gameState);

                const normalizedData = Array.isArray(rawData)
                ? rawData.map((t: any) => ({
                        id: t.ID,
                        name: t.Name,
                        prize: t.Prize,
                        date: t.Date,
                        time: t.Time,
                        isJoined: t.IsJoined,
                    }))
                : [];

                setJoinedSessions(normalizedData);
            } catch (error) {
                console.error("error fetching upcoming joined tournaments");
            }
        };
        fetchTournaments();
    }, [firebaseUser, loading]);

    return(
        <>
            <div className={`viewContainer ${!joinedSessions || joinedSessions.length === 0 ? 'empty' : ''}`}>
            {joinedSessions && joinedSessions.length > 0 ? (
            joinedSessions.map((tourney, index) => (
                <div key={index} className="joinedSessionEntry">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ whiteSpace: 'nowrap', color: 'rgba(236,240,241,.7)', fontSize: '10px' }}>
                            <CountdownTimer date={tourney.date} time={tourney.time} />
                        </div>
                        <div>{tourney.name} - ${tourney.prize} </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => setTakeToLobby(tourney.id)} className="btn btn-primary">
                                    Join Lobby
                                </button>
                                <button onClick={() => openLeaveModal(tourney)} className="btn btn-danger">Leave</button>
                            </div>
                        </div>
                    </div>
                </div>
            )))
            :
            (
                <div style={{ color: 'rgba(236,240,241,.7)', fontSize: '12px', padding: '12px', paddingBottom: '100px' }}>
                    {loading ? 'loading...' : 'No Tournaments Joined.'}
                </div>
            )}
            </div>
            {showLeaveModal && (
                <JoinLeaveModal
                    accept={handleLeave}
                    closeModal={closeLeaveModal}
                    selectedSession={selectedTournament}
                />
            )}
        </>
    )
}

export default JoinedSessions
